/**
 * Centers Service
 *
 * Handles center-related API calls.
 */

import api from '../utils/api';

/**
 * Get all centers
 * GET /v1/centers/
 * @returns {Promise<{ success: boolean, data?: array, error?: string }>}
 */
export const getCenters = async () => {
  try {
    const response = await api.get('/v1/centers/');

    const list = Array.isArray(response) ? response : response?.data ?? [];
    if (Array.isArray(list)) {
      return {
        success: true,
        data: list,
        message: 'Centers retrieved successfully',
      };
    }

    if (response?.status === 'success' && response?.data) {
      return {
        success: true,
        data: response.data,
        message: response.message || 'Centers retrieved successfully',
      };
    }

    return {
      success: false,
      error: response?.message || 'Failed to retrieve centers',
      data: null,
    };
  } catch (error) {
    console.error('Get centers error:', error);
    const errorMessage =
      error?.response?.message ||
      error?.response?.error ||
      error?.message ||
      'Failed to retrieve centers. Please try again.';
    return {
      success: false,
      error: errorMessage,
      data: null,
    };
  }
};

const PHASE_STATUSES = ['inactive', 'active'];

const normalizePhase = (phase) => {
  if (!phase || typeof phase !== 'object') return null;
  const name = phase.name && String(phase.name).trim();
  if (!name) return null;

  const description = phase.description && String(phase.description).trim()
    ? String(phase.description).trim()
    : '';
  const parsedAmount = Number(phase.amount);
  const amount = Number.isFinite(parsedAmount) ? parsedAmount : 0;
  const due_date = phase.due_date && String(phase.due_date).trim() ? String(phase.due_date).trim() : null;
  const status = phase.status && PHASE_STATUSES.includes(phase.status) ? phase.status : 'inactive';

  return {
    name,
    description,
    amount,
    due_date,
    status,
  };
};

const normalizePhases = (phases) => {
  if (!Array.isArray(phases)) return undefined;
  const normalized = phases.map(normalizePhase).filter(Boolean);
  return normalized.length ? normalized : undefined;
};

/**
 * Create a center
 * POST /v1/centers/
 * @param {Object} payload - { name, full_name, region_id, phases? }
 */
export const createCenter = async (payload) => {
  try {
    const phases = normalizePhases(payload.phases);
    const body = {
      name: payload.name?.trim(),
      full_name: payload.full_name?.trim(),
      region_id: payload.region_id || undefined,
    };
    if (phases) body.phases = phases;
    const response = await api.post('/v1/centers/', body);
    const center = response?.data ?? response;
    if (response?.status === 'success' && center) {
      return { success: true, data: center, message: response.message || 'Center created successfully' };
    }
    if (center?.id) {
      return { success: true, data: center, message: 'Center created successfully' };
    }
    return { success: false, error: response?.message || 'Failed to create center', data: null };
  } catch (error) {
    console.error('Create center error:', error);
    const errorMessage = error?.response?.message || error?.response?.error || error?.message || 'Failed to create center. Please try again.';
    return { success: false, error: errorMessage, data: null };
  }
};

/**
 * Update a center
 * PATCH /v1/centers/{center_id}
 */
export const updateCenter = async (centerId, payload) => {
  try {
    const body = {};
    if (payload.name !== undefined) body.name = payload.name?.trim();
    if (payload.full_name !== undefined) body.full_name = payload.full_name?.trim();
    if (payload.region_id !== undefined) body.region_id = payload.region_id || null;
    const phases = normalizePhases(payload.phases);
    if (Array.isArray(payload.phases)) body.phases = phases || [];
    const response = await api.patch(`/v1/centers/${centerId}`, body);
    const center = response?.data ?? response;
    if (response?.status === 'success' && center) {
      return { success: true, data: center, message: response.message || 'Center updated successfully' };
    }
    if (center?.id) {
      return { success: true, data: center, message: 'Center updated successfully' };
    }
    return { success: false, error: response?.message || 'Failed to update center', data: null };
  } catch (error) {
    console.error('Update center error:', error);
    const errorMessage = error?.response?.message || error?.response?.error || error?.message || 'Failed to update center. Please try again.';
    return { success: false, error: errorMessage, data: null };
  }
};

/**
 * Delete a center
 * DELETE /v1/centers/{center_id}
 */
export const deleteCenter = async (centerId) => {
  try {
    const response = await api.delete(`/v1/centers/${centerId}`);
    if (response?.status === 'success' || response?.message) {
      return { success: true, message: response.message || 'Center deleted successfully' };
    }
    return { success: false, error: response?.message || response?.error || 'Failed to delete center' };
  } catch (error) {
    console.error('Delete center error:', error);
    const errorMessage = error?.response?.message || error?.response?.error || error?.message || 'Failed to delete center. It may be in use by students.';
    return { success: false, error: errorMessage };
  }
};

export { PHASE_STATUSES };
const centersService = {
  getCenters,
  createCenter,
  updateCenter,
  deleteCenter,
};
export default centersService;
